// Slot → CSS. The whole reason a new theme is a data change.
//
// Every function here translates ONE typed slot from backend/bio/spec.py
// into inline style. Nothing in this file knows a preset by name, and
// nothing branches on one — that property is asserted by
// backend/tests/test_bio_presets.py, which greps the renderer for every
// shipped preset id and renders a synthetic sixth preset that exercises
// slots the five do not.
//
// Why inline styles rather than Tailwind: a preset carries arbitrary hex,
// arbitrary rem sizes and arbitrary radii. Tailwind's JIT cannot safelist
// values it never sees at build time — the old renderer already had a
// comment about this defeating it for gradients. Layout stays Tailwind;
// anything a preset controls is inline.

/** Font families the renderer can load. Mirrors FONTS in bio/spec.py —
 *  asserted by test_font_map_matches_the_backend_font_list. */
export const FONT_MAP = {
  Inter: "'Inter', sans-serif",
  Poppins: "'Poppins', sans-serif",
  "Playfair Display": "'Playfair Display', Georgia, serif",
  "Instrument Serif": "'Instrument Serif', Georgia, serif",
  "Work Sans": "'Work Sans', sans-serif",
  "DM Sans": "'DM Sans', sans-serif",
};

/** Resolve a font ROLE ("display" | "body" | "ui") to a CSS stack. */
export function fontStack(fonts, role) {
  const family = (fonts && fonts[role]) || "Inter";
  return FONT_MAP[family] || FONT_MAP.Inter;
}

/** Every family this page needs — for the <link> loader. Deduped. */
export function fontsUsed(style) {
  const f = style?.fonts || {};
  return Array.from(new Set([f.display, f.body, f.ui].filter(Boolean)));
}

/** `{width, color}` → a CSS border. width 0 IS "no border". */
export function borderCss(border) {
  if (!border || !border.width) return "none";
  return `${border.width}px solid ${border.color}`;
}

/** `{x, y, blur, color}` → a CSS box-shadow. Covers both the diffuse and
 *  the hard-offset shapes; `null` means none. */
export function shadowCss(shadow) {
  if (!shadow) return "none";
  return `${shadow.x}px ${shadow.y}px ${shadow.blur}px ${shadow.color}`;
}

/** `{type, color, angle, stops}` → a CSS background value, or "" for none. */
export function fillCss(fill) {
  if (!fill || fill.type === "none") return "";
  if (fill.type === "solid") return fill.color || "";
  if (fill.type === "linear" && fill.stops?.length >= 2) {
    return `linear-gradient(${fill.angle ?? 180}deg, ${fill.stops.join(", ")})`;
  }
  return "";
}

/** A radius slot → CSS. 999 is the pill convention. */
export function radiusCss(radius) {
  return `${radius ?? 0}px`;
}

/**
 * Per-item entrance animation.
 *
 * The designs cascade items in (`140 + i × stagger`); firing everything at
 * once loses the effect entirely, which is what the old renderer did.
 * `index` may be negative for header elements that lead the cascade.
 */
export function animCss(animation, index, enabled = true) {
  if (!enabled || !animation || animation.kind === "none") return {};
  const name = animation.kind === "slide" ? "tdSlide" : "tdFade";
  const delay = Math.max(0, 140 + index * (animation.stagger_ms || 0));
  return {
    animation: `${name} ${animation.duration_ms || 500}ms cubic-bezier(.2,.8,.2,1) both`,
    animationDelay: `${delay}ms`,
  };
}

/** A TextStyle slot → CSS. Sizes are rem, deliberately — see spec.py. */
export function textCss(t, fonts) {
  if (!t) return {};
  return {
    fontFamily: fontStack(fonts, t.font),
    fontSize: `${t.size}rem`,
    fontWeight: t.weight,
    letterSpacing: `${t.spacing}em`,
    textTransform: t.upper ? "uppercase" : "none",
  };
}

/**
 * The full style for ANY ButtonSpec slot — `button`, `shop.buy`, `shop.more`.
 *
 * Takes the slot rather than reading `style.button`, because a shop page
 * paints three different ButtonSpecs and they must not share values.
 *
 * `pad_v` is the slot's own vertical padding; `null` falls back to
 * `spacing.btn_pad_v`, which is what every full-width page button wants.
 * This replaces a read of `b.padTopBottom` — a field `extra="forbid"`
 * made impossible, so that branch could never fire and the fallback was
 * the only behaviour.
 */
export function buttonCssFor(b, style, { fullWidth = true, hpad = 1.125 } = {}) {
  const bg = fillCss(b.fill);
  return {
    ...textCss({ size: b.size, weight: b.weight, spacing: b.spacing,
                 upper: b.upper, font: b.font }, style.fonts),
    padding: `${b.pad_v ?? style.spacing.btn_pad_v}rem ${hpad}rem`,
    borderRadius: radiusCss(b.radius),
    background: bg || "transparent",
    color: b.label,
    border: borderCss(b.border),
    boxShadow: shadowCss(b.shadow),
    ...(b.underline ? { textDecoration: "underline", textUnderlineOffset: "3px" } : {}),
    ...(b.blur ? { backdropFilter: `blur(${b.blur}px)`,
                   WebkitBackdropFilter: `blur(${b.blur}px)` } : {}),
    textAlign: "center",
    lineHeight: 1.25,
    display: "block",
    ...(fullWidth ? { width: "100%" } : {}),
  };
}

/** The page's link button. Unchanged behaviour, now one call deep. */
export function buttonCss(style) {
  return buttonCssFor(style.button, style);
}

/** Social chip. `box: 0` renders a bare glyph with no chip behind it. */
export function socialCss(style) {
  const s = style.social;
  const bare = !s.box;
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: bare ? "auto" : `${s.box}rem`,
    height: bare ? "auto" : `${s.box}rem`,
    borderRadius: radiusCss(s.radius),
    background: bare ? "transparent" : s.bg,
    border: bare ? "none" : borderCss(s.border),
    color: s.color,
  };
}

export function avatarCss(style) {
  const a = style.avatar;
  return {
    width: `${a.size}rem`,
    height: `${a.size}rem`,
    borderRadius: radiusCss(a.radius),
    border: borderCss(a.ring),
    padding: `${a.pad}rem`,
    boxSizing: "content-box",
    objectFit: "cover",
    backgroundColor: style.canvas.dark ? "#1A1D26" : "rgba(0,0,0,0.05)",
  };
}

export function tileCss(style) {
  const t = style.tile;
  return {
    borderRadius: radiusCss(t.radius),
    border: borderCss(t.border),
    boxShadow: shadowCss(t.shadow),
    backgroundColor: style.canvas.dark ? "#1A1D26" : "rgba(0,0,0,0.06)",
    display: "block",
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "cover",
  };
}

/** Caption style. `position: "inside"` overlays the image. */
export function captionCss(style) {
  const c = style.tile.caption;
  const base = {
    ...textCss({ size: c.size, weight: c.position === "inside" ? 600 : 500,
                 spacing: c.spacing, upper: c.upper, font: c.font }, style.fonts),
    color: c.color,
    lineHeight: 1.25,
  };
  if (c.position === "inside") {
    return {
      ...base,
      position: "absolute",
      left: "0.5rem",
      right: "0.5rem",
      bottom: "0.5rem",
      textShadow: "0 1px 6px rgba(0,0,0,0.6)",
    };
  }
  return { ...base, marginTop: "0.25rem", textAlign: c.upper ? "left" : "center" };
}

/** How many tiles per row for a given layout. Layout B is TWO columns —
 *  the design's grid-only layout is deliberately larger than the mixed
 *  layout's 3-up. */
export function tileColumns(layoutType) {
  return layoutType === "instagram" ? 2 : 3;
}

// ── Shop ──────────────────────────────────────────────────────────────
// A product card is not a photo tile, and every difference below is a
// difference the tile slots cannot express. See backend/bio/spec.py.

/** Width at which the product grid goes from 2 columns to 3. */
export const SHOP_3UP_MIN_WIDTH = 640;

/**
 * Products shown before "See more", when the style does not say.
 *
 * ONE constant, because there are two readers: BioPageRenderer draws the
 * grid and BioEditorPage's note explains it. They had separate literals
 * — `?? 6` in the renderer and `?? 4` in the editor — so a style
 * carrying no `visible` rendered six products under a note promising
 * four, and the number the creator was told was not the number their
 * visitors got (P, 2026-08-24).
 *
 * Matches `ShopSpec.visible`'s default in backend/bio/spec.py. A preset
 * that sets its own value still wins; this is only the missing case.
 */
export const SHOP_VISIBLE_FALLBACK = 4;

/**
 * Columns for the product grid, from the RENDER WIDTH — never from the
 * preset.
 *
 * MEASURED, not chosen: at 375px, three columns leaves 71–84px of content
 * width once each preset's `spacing.hpad`, the card padding and the card
 * border are removed. A "BUY NOW" label needs ~82px in Quiet Margin, so it
 * clips; and real catalogue titles ("Walnut cutting board") wrap to three
 * and four lines at that width, giving every row a different height.
 *
 * Two columns gives 125–141px, where labels fit and titles settle to one
 * or two lines.
 *
 * This is deliberately NOT a slot. A preset that carried a column count
 * could choose an arrangement that does not fit the device the page is
 * read on, and `shop.visible` already covers the part that IS an
 * editorial decision — how many products show before the reveal.
 */
export function shopColumns(width) {
  return width >= SHOP_3UP_MIN_WIDTH ? 3 : 2;
}

const RATIO = { square: "1 / 1", portrait: "4 / 5", landscape: "4 / 3" };

/** The product card container. Unlike a tile this has a themeable fill,
 *  because text is read on it. */
export function shopCardCss(style) {
  const c = style.shop.card;
  return {
    background: fillCss(c.fill) || "transparent",
    border: borderCss(c.border),
    borderRadius: radiusCss(c.radius),
    boxShadow: shadowCss(c.shadow),
    padding: `${c.pad}rem`,
    ...(c.blur ? { backdropFilter: `blur(${c.blur}px)`,
                   WebkitBackdropFilter: `blur(${c.blur}px)` } : {}),
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
    minWidth: 0,
  };
}

/** The product shot. `fit: "contain"` lets a product on a white
 *  background letterbox instead of being cropped. */
export function shopImageCss(style) {
  const i = style.shop.image;
  return {
    borderRadius: radiusCss(i.radius),
    border: borderCss(i.border),
    aspectRatio: RATIO[i.ratio] || RATIO.square,
    width: "100%",
    objectFit: i.fit,
    display: "block",
    backgroundColor: style.canvas.dark ? "#1A1D26" : "rgba(0,0,0,0.06)",
  };
}

/** Title or price. These carry their own colour because they sit on the
 *  CARD, not on the canvas. */
export function shopTextCss(style, which) {
  const t = style.shop[which];
  return { ...textCss(t, style.fonts), color: t.color };
}

/** The buy CTA inside a card. Narrower horizontal padding than a
 *  page-level button — it lives in a grid cell, not a full-width row. */
export function shopBuyCss(style) {
  return buttonCssFor(style.shop.buy, style, { hpad: 0.5 });
}

/** "See more". Ranks BELOW buy by construction — that is why it is its
 *  own slot rather than a reuse of `button`. */
export function shopMoreCss(style) {
  return buttonCssFor(style.shop.more, style, { hpad: 1 });
}
